import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DEFAULT_PAGE_SIZE } from '@common/components/constants';
import { COMMON_TRANSLATION } from '@common/constants';

import {
  PaginationContainerStyled,
  StyledError,
  StyledNotResultsCellText,
  StyledNotResultsRowText,
  StyledNotResultsText,
  TableWrapper,
} from './PaginatedTable.styles';
import { PaginatedTableProps } from './PaginatedTable.types';
import { TableHeader } from './TableHeader/TableHeader';
import { TableBodyRows } from './TableBodyRows/TableBodyRows';
import Pagination from '../Pagination/Pagination';
import PaginationResults from '../../molecules/PaginationResults/PaginationResults';
import Loader from '../Loader/Loader';
import { TableBody } from '../../atoms';

const PaginatedTable: FC<PaginatedTableProps<any>> = ({
  columns,
  data,
  onSortingChange,
  onPageChange,
  onRowClick,
  isPaginated = true,
  pageSize = DEFAULT_PAGE_SIZE,
  rowClickable = false,
}: PaginatedTableProps<any>) => {
  const { t } = useTranslation([COMMON_TRANSLATION]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [firstItemPage, setFirstItemPage] = useState<number>(0);
  const [lastItemPage, setLastItemPage] = useState<number>(0);

  const [isLoading, setIsLoading] = useState<boolean>();
  const [isError, setIsError] = useState<boolean>();
  const [offset, setOffset] = useState<number>();
  const [totalCount, setTotalCount] = useState<number>();
  const [rows, setRows] = useState<any[] | null>();

  const handleChangeSorting = (sorting: Map<string, boolean>): void => {
    onSortingChange?.(sorting);
  };

  const handlePageChange = (page: number): void => {
    const offset = (page - 1) * pageSize;
    onPageChange?.(offset);
  };

  useEffect(() => {
    if (data) {
      setRows(data.rows);
      setIsLoading(data.loading);
      setIsError(data.error);
      setOffset(data.offset ?? 0);
      setTotalCount(data.totalCount ?? 0);
    }
  }, [pageSize, data, offset, isPaginated]);

  useEffect(() => {
    if (offset !== undefined && totalCount !== undefined && pageSize !== undefined) {
      const firstItem = offset + 1;
      const lastItem = offset + pageSize > totalCount ? totalCount : offset + pageSize;
      setCurrentPage(offset / pageSize + 1);
      setFirstItemPage(firstItem);
      setLastItemPage(lastItem);
    }
  }, [offset, totalCount, pageSize, isPaginated]);

  return (
    <>
      <TableWrapper>
        <TableHeader sortingEnabled={!!rows?.length} columns={columns} onSortingChange={handleChangeSorting} />
        <TableBody>
          {!isLoading && !isError && !!rows?.length ? (
            <TableBodyRows
              data-testid="paginated-table-rows"
              data={rows}
              columns={columns}
              rowClickable={rowClickable}
              onRowClick={onRowClick}
            />
          ) : (
            <StyledNotResultsRowText>
              <StyledNotResultsCellText colSpan={columns.length}>
                {isLoading ? (
                  <Loader css={{ height: '520px' }} />
                ) : isError ? (
                  <StyledError>{t('errors.generic', { ns: COMMON_TRANSLATION })}</StyledError>
                ) : !rows?.length ? (
                  <StyledNotResultsText data-testid="paginated-table-no-results">
                    {t('no_results', { ns: COMMON_TRANSLATION })}
                  </StyledNotResultsText>
                ) : null}
              </StyledNotResultsCellText>
            </StyledNotResultsRowText>
          )}
        </TableBody>
      </TableWrapper>
      {isPaginated && data && !!totalCount && (
        <PaginationContainerStyled>
          <PaginationResults range={[firstItemPage, lastItemPage]} totalCount={totalCount} />
          {totalCount > pageSize && (
            <Pagination
              currentPage={currentPage}
              totalCount={totalCount}
              pageSize={pageSize}
              onPageChange={handlePageChange}
            />
          )}
        </PaginationContainerStyled>
      )}
    </>
  );
};

export default PaginatedTable;
